import axios from "axios";
import { resolveUserApiV1Base } from "../../api/resolveBaseUrl";
import { extractChatGroupIdFromAxiosError, isChatForbiddenResponse, redirectAfterChatGroupAccessLost, redirectToErrorPageOnce, } from "../../helper/chatAccessRedirect";
import { isAuthRedirectInFlight } from "../../helper/sessionRedirect";
const adminDomain = process.env.VUE_APP_ADMIN_API_BASE_URL;
function stableStringify(value) {
    if (value == null)
        return "";
    if (typeof value !== "object")
        return String(value);
    if (Array.isArray(value))
        return `[${value.map(stableStringify).join(",")}]`;
    const obj = value;
    const keys = Object.keys(obj).sort();
    return `{${keys.map((k) => `${k}:${stableStringify(obj[k])}`).join(",")}}`;
}
export default class BaseService {
    /** Dedupe các GET trùng nhau đang in-flight (url + params). */
    static inFlightGets = new Map();
    static async getByUrl(uri, params = {}, _apiType = "USER") {
        try {
            const key = `GET:${uri}?${stableStringify(params)}`;
            const existing = this.inFlightGets.get(key);
            if (existing)
                return await existing;
            const p = axios
                .get(uri, { params })
                .catch((e) => this.errorMsg(e))
                .finally(() => {
                this.inFlightGets.delete(key);
            });
            this.inFlightGets.set(key, p);
            return await p;
        }
        catch (e) {
            return this.errorMsg(e);
        }
    }
    static async get(uri, params = {}, apiType = "USER") {
        try {
            const url = this.getUrl(uri, apiType);
            const key = `GET:${url}?${stableStringify(params)}`;
            const existing = this.inFlightGets.get(key);
            if (existing)
                return await existing;
            const p = axios
                .get(url, { params })
                .catch((e) => this.errorMsg(e))
                .finally(() => {
                this.inFlightGets.delete(key);
            });
            this.inFlightGets.set(key, p);
            return await p;
        }
        catch (e) {
            return this.errorMsg(e);
        }
    }
    static async post(uri, params = {}, apiType = "USER") {
        try {
            return await axios.post(this.getUrl(uri, apiType), params);
        }
        catch (e) {
            return this.errorMsg(e);
        }
    }
    static async put(uri, params = {}, apiType = "USER") {
        try {
            return await axios.put(this.getUrl(uri, apiType), params);
        }
        catch (e) {
            return this.errorMsg(e);
        }
    }
    static async patch(uri, params = {}, apiType = "USER") {
        try {
            return await axios.patch(this.getUrl(uri, apiType), params);
        }
        catch (e) {
            return this.errorMsg(e);
        }
    }
    static async show(uri) {
        try {
            return await axios.get(this.getUrl(uri));
        }
        catch (e) {
            return this.errorMsg(e);
        }
    }
    static async delete(uri) {
        try {
            return await axios.delete(this.getUrl(uri));
        }
        catch (e) {
            return this.errorMsg(e);
        }
    }
    static getUrl(uri, apiType = "USER") {
        let domainUrl = resolveUserApiV1Base();
        if (apiType === "ADMIN") {
            domainUrl = adminDomain ?? "";
        }
        return domainUrl + uri;
    }
    static errorMsg(e) {
        if (!axios.isAxiosError(e)) {
            if (process.env.NODE_ENV !== "production") {
                console.warn("[API]", e instanceof Error ? e.message : e);
            }
            return { data: null };
        }
        const status = e.response?.status;
        const app = typeof window !== "undefined" ? window.app : undefined;
        if (status == null) {
            if (process.env.NODE_ENV !== "production") {
                console.warn("[API]", e.message || e);
            }
            return { data: null };
        }
        if (status === 422) {
            return { data: e.response?.data };
        }
        if (status === 401) {
            const path = typeof window !== "undefined"
                ? String(window.location.pathname || "").toLowerCase()
                : "";
            const onLoginPage = path === "/login" ||
                path.startsWith("/login/") ||
                path === "/agency-login" ||
                path.startsWith("/agency-login/");
            if (!onLoginPage && !isAuthRedirectInFlight() && app?.$store) {
                app.$store.dispatch("redirectToLogin");
            }
            return;
        }
        if (status === 403) {
            const responseData = e.response?.data;
            const data = responseData?.data;
            if (data?.reason === "company_access_host_mismatch" &&
                typeof data.redirect_to === "string" &&
                data.redirect_to &&
                typeof window !== "undefined") {
                const target = data.redirect_to;
                const sameOrigin = (() => {
                    try {
                        return new URL(target).origin === window.location.origin;
                    }
                    catch {
                        return false;
                    }
                })();
                if (!sameOrigin) {
                    window.location.replace(target);
                    return { data: e.response?.data };
                }
            }
            const requestUrl = String(e.config?.url || e.config?.baseURL || "");
            if (isChatForbiddenResponse(status, responseData, requestUrl)) {
                redirectAfterChatGroupAccessLost({
                    groupId: extractChatGroupIdFromAxiosError(e),
                    router: app?.$router,
                    store: app?.$store,
                });
            }
            else if (app?.$router) {
                redirectToErrorPageOnce(app.$router);
            }
            return { data: e.response?.data };
        }
        return { data: e.response?.data };
    }
}
